/**
 * FAQ ページ（公開）
 * カテゴリ別に Q&A を展開
 */
import type { ReactNode } from "react";
import { PublicHeader, PublicFooter } from "./LandingPage.js";

const PRIMARY = "#534AB7";

// 回答にリンクを含める場合があるため ReactNode を許容
const FAQS: Array<{ category: string; items: Array<{ q: string; a: ReactNode }> }> = [
  {
    category: "プランと契約",
    items: [
      {
        q: "料金プランを教えてください",
        a: "Starter（¥4,980/月、月 30 件）、Professional（¥9,800/月、月 100 件）、Enterprise（¥19,800/月、送信無制限）の 3 プランをご用意しています。表示価格はすべて税別で、決済時に消費税 10% が加算されます。ご登録から 14 日間は無料トライアルとして、Professional プラン相当（月 100 件）までお試しいただけます。",
      },
      {
        q: "無料トライアル中に料金は発生しますか？",
        a: "発生しません。無料トライアルはご登録時に自動で始まり、クレジットカードの登録は不要です。トライアル終了によって自動で課金されることはありません。トライアル終了後も書類を送信するには、管理画面の「プラン・請求」からプランにお申し込みください（お申し込みいただいた日から課金が始まります）。",
      },
      {
        q: "月間送信数の上限を超えるとどうなりますか？",
        a: "送信 API が制限に達した旨を返し、その請求期間（お申し込み日を起点とした 1 か月）は追加の送信ができなくなります。上位プランに変更いただくと即座に上限が引き上がります（日割り計算で課金）。",
      },
      {
        q: "プランの変更・解約はいつでも可能ですか？",
        a: "はい、いつでも Stripe カスタマーポータルから変更・解約が可能です。ダウングレード・アップグレードは即時反映、解約は契約期間終了時に有効となります。",
      },
      {
        q: "支払い方法を教えてください",
        a: "基本的にクレジットカード決済のみとなります。",
      },
      {
        q: "領収書・請求書は発行されますか？",
        a: "Stripe から自動でメール送付されます。ダッシュボードの「プラン・請求」タブから過去分の請求履歴・領収書も確認・ダウンロード可能です。",
      },
    ],
  },
  {
    category: "HubSpot との連携",
    items: [
      {
        q: "HubSpot のどのプランで利用できますか？",
        a: "HubSpot のすべてのプラン（Free を含む）でご利用いただけます。OAuth 接続・ステータス自動同期・CRM カード（レコード画面の送信ボタン）すべて全プラン対応です。View-Only Seat の利用者も CRM カードから書類送信を実行できます。",
      },
      {
        q: "HubSpot のどのオブジェクトに対応していますか？",
        a: "取引（Deals）、会社（Companies）、コンタクト（Contacts）の 3 種類に対応しています。それぞれのレコード画面からワンクリックで送信できます。",
      },
      {
        q: "プロパティはどうやって作成しますか？",
        a: "ダッシュボードの「接続設定」タブの「プロパティの一括作成」から、必要なプロパティを一括作成できます。既存のプロパティを指定することも可能です。",
      },
      {
        q: "HubSpot のワークフローと連携できますか？",
        a: "はい。契約ステータスが HubSpot プロパティに同期されるため、「締結完了 → Slack 通知」「却下 → タスク作成」などのワークフローを自由に組めます。",
      },
    ],
  },
  {
    category: "クラウドサインとの連携",
    items: [
      {
        q: "クラウドサインのアカウントは別途必要ですか？",
        a: "はい、クラウドサインのスタンダードプラン以上のアカウントが別途必要です。本サービスは HubSpot とクラウドサインを連携するための拡張アプリです。",
      },
      {
        q: "クラウドサインの Web API を使いますか？",
        a: "はい、クラウドサインの Web API を使用します。クラウドサイン管理画面からクライアント ID を発行し、本サービスに登録することで連携が完了します。",
      },
      {
        q: "複数の送信者アカウントを使い分けられますか？",
        a: "はい、営業部・法務部・グループ会社など、複数のクラウドサイン送信者アカウントを登録し、送信時に使い分けることができます。",
      },
      {
        q: "送信できるファイル形式は？",
        a: "クラウドサインの仕様に従い、PDF ファイルのみ送信可能です。1 ファイルあたり最大 5MB までです。",
      },
    ],
  },
  {
    category: "運用・機能",
    items: [
      {
        q: "締結ステータスはどのくらいの頻度で同期されますか？",
        a: "15 分間隔で自動同期されます。クラウドサイン側のステータス変化を 15 分以内に HubSpot に反映します。",
      },
      {
        q: "締結済みの PDF は HubSpot に保存されますか？",
        a: "はい。「HubSpot へ再アップロード」モードを選択すれば、締結完了時に署名済み PDF と合意締結証明書が HubSpot Files に自動保存されます。URL のみ保存するモードも選択できます。",
      },
      {
        q: "固定の送付宛先（社内承認者など）を設定できますか？",
        a: "はい。「接続設定」タブの「デフォルト設定」→「送付宛先」内の「固定送付宛先」で、氏名とメールアドレスを登録できます。全送信で自動的に追加され、社内承認→外部送付の順序で署名ワークフローを組めます。",
      },
      {
        q: "共有先（CC）の指定はできますか？",
        a: "可能です。送信モーダルから手動入力、または HubSpot のプロパティから自動取得するいずれかの方式を選択できます。",
      },
      {
        q: "送信順（署名順）は制御できますか？",
        a: "はい。送信モーダルで宛先の順序を上下ボタンまたはドラッグ＆ドロップで変更できます。リストの上から順に署名依頼メールが届きます。",
      },
    ],
  },
  {
    category: "セキュリティ",
    items: [
      {
        q: "HubSpot や クラウドサインの認証情報は安全ですか？",
        a: "認証情報はサーバー側で暗号化して保存し、通信は常時 HTTPS で行います。ダッシュボードの画面上に機密情報が表示されることもありません。",
      },
      {
        q: "送信した書類のデータはどこに保存されますか？",
        a: "書類本体（PDF）はクラウドサインとHubSpot Files にのみ保存されます。本サービスは送信履歴・ステータス・書類 ID などのメタデータのみをデータベースに保存します。",
      },
      {
        q: "ユーザー権限の設定はできますか？",
        a: "オーナー / 管理者 / メンバー の 3 ロールでユーザーを管理できます。ユーザー招待・ロール変更・削除は「ユーザー」タブから操作可能です。",
      },
    ],
  },
  {
    category: "サポート",
    items: [
      {
        q: "お問い合わせ窓口は？",
        a: (
          <>
            <a href="https://cs.hsextension.com/contact" style={{ color: "#534AB7", textDecoration: "underline" }}>
              https://cs.hsextension.com/contact
            </a>
            {" "}のフォームよりお問い合わせください。営業日 1〜2 日以内にご返信いたします。
          </>
        ),
      },
      {
        q: "導入支援はありますか？",
        a: "ナウビレッジ株式会社では HubSpot Diamond Partner としての実績を活かし、初期設定から運用定着までの導入支援も提供しています。詳細はお問い合わせください。",
      },
    ],
  },
];

export default function FaqPage() {
  return (
    <div style={{ fontFamily: "system-ui, -apple-system, sans-serif", color: "#1a1a1a", background: "white" }}>
      <PublicHeader minimal />

      <section style={{ background: "linear-gradient(135deg, #534AB7 0%, #7F77DD 100%)", color: "white", padding: "60px 24px 50px", textAlign: "center" }}>
        <h1 style={{ fontSize: 36, fontWeight: 800, marginBottom: 12 }}>よくあるご質問</h1>
        <p style={{ fontSize: 16, opacity: 0.95 }}>
          Sign Extension for クラウドサイン に関するよくあるご質問をまとめました。
        </p>
      </section>

      <section style={{ padding: "60px 24px", background: "#faf9f7" }}>
        <div style={{ maxWidth: 840, margin: "0 auto" }}>
          {FAQS.map((cat) => (
            <div key={cat.category} style={{ marginBottom: 40 }}>
              <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 16, color: PRIMARY, borderLeft: `4px solid ${PRIMARY}`, paddingLeft: 12 }}>
                {cat.category}
              </h2>
              {cat.items.map((item, i) => (
                <details key={i} style={{ background: "white", borderRadius: 10, marginBottom: 10, border: "1px solid #e8e7e3" }}>
                  <summary style={{ padding: "16px 24px", fontSize: 15, fontWeight: 600, cursor: "pointer", listStyle: "none", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span>Q. {item.q}</span>
                    <span style={{ color: "#999", fontSize: 12 }}>▼</span>
                  </summary>
                  <div style={{ padding: "16px 24px 20px", color: "#4a4a48", lineHeight: 1.9, fontSize: 14, borderTop: "1px solid #f0efeb" }}>
                    A. {item.a}
                  </div>
                </details>
              ))}
            </div>
          ))}

          <div style={{ textAlign: "center", marginTop: 60, padding: "32px 24px", background: "white", borderRadius: 12 }}>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>解決しない場合はお気軽にお問い合わせください</div>
            <div style={{ fontSize: 14, color: "#6b6b68", marginBottom: 20 }}>1〜2 営業日以内にご返信いたします</div>
            <a href="/contact" style={{ display: "inline-block", padding: "12px 28px", background: PRIMARY, color: "white", borderRadius: 10, textDecoration: "none", fontWeight: 600 }}>
              お問い合わせフォーム
            </a>
          </div>
        </div>
      </section>

      <PublicFooter minimal />
    </div>
  );
}
